import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../utils/api';

type OfferRow = {
  id: string;
  status: string;
  playDate: string;
  timeFrom: string;
  timeTo: string;
  confirmationMode: string;
  club?: { id: string; name: string } | null;
  author?: { playerId: string; name: string | null } | null;
  selectedPlayer?: { playerId: string; name: string } | null;
  responsesCount: number;
  notifiedPlayersCount: number;
  createdAt: string;
};

type OfferDetail = {
  id: string;
  status: string;
  playDate: string;
  timeFrom: string;
  timeTo: string;
  club?: { id: string; name: string } | null;
  author?: { playerId: string; name: string | null; levelCode?: string | null; pwr?: number | null } | null;
  selectedPlayer?: { playerId: string; name: string } | null;
  responsesCount: number;
  notifiedPlayersCount: number;
  responses?: Array<{
    id: string;
    status: string;
    respondedAt: string | null;
    player: { playerId: string; name: string; levelCode: string | null; pwr: number | null };
    pwrDelta: number | null;
  }>;
  deliveries?: Array<{
    id: string;
    recipientName: string | null;
    waveNumber: number;
    deliveryStatus: string;
    responseStatus: string | null;
    retryCount: number;
    lastError: string | null;
    sentAt: string | null;
    editedAt: string | null;
  }>;
};

const fmt = (v?: string | null) => (v ? new Date(v).toLocaleString('ru-RU') : '—');

const PlayAdmin: React.FC = () => {
  const [rows, setRows] = useState<OfferRow[]>([]);
  const [detail, setDetail] = useState<OfferDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [authorQuery, setAuthorQuery] = useState('');
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams();
      if (status) query.set('status', status);
      if (authorQuery.trim()) query.set('authorQuery', authorQuery.trim());
      query.set('limit', '50');
      const data = await apiGet<{ success: boolean; data: { items: OfferRow[] } }>(`/play/admin/offers?${query.toString()}`);
      setRows(data?.data?.items || []);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить TTM PLAY offers');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const data = await apiGet<{ success: boolean; data: OfferDetail }>(`/play/admin/offers/${id}`);
      setDetail(data.data);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить detail');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const askReason = (title: string): string | null => {
    const reason = window.prompt(`${title}\n\nУкажите причину:`);
    if (!reason || !reason.trim()) return null;
    return reason.trim();
  };

  const runAdminAction = async (endpoint: string, payload: Record<string, unknown>) => {
    if (!detail) return;
    setActionLoading(true);
    setError('');
    try {
      await apiPost(endpoint, payload);
      await openDetail(detail.id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Не удалось выполнить admin action');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">TTM PLAY Admin</h2>
        <p className="text-sm text-gray-600 mt-1">Список предложений, отклики, доставки и ошибки Telegram.</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 flex gap-2 items-end flex-wrap">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <input
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded px-2 py-1"
            placeholder="OPEN"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Author</label>
          <input
            value={authorQuery}
            onChange={(e) => setAuthorQuery(e.target.value)}
            className="border rounded px-2 py-1"
            placeholder="player name"
          />
        </div>
        <button onClick={load} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Применить
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600">
                <th className="px-3 py-2 text-left">Дата</th>
                <th className="px-3 py-2 text-left">Автор</th>
                <th className="px-3 py-2 text-left">Клуб</th>
                <th className="px-3 py-2 text-left">Статус</th>
                <th className="px-3 py-2 text-left">Отклики</th>
                <th className="px-3 py-2 text-left">Увед.</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="px-3 py-3 text-gray-500" colSpan={6}>Загрузка...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td className="px-3 py-3 text-gray-500" colSpan={6}>Нет данных</td>
                </tr>
              )}
              {!loading && rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => openDetail(row.id)}
                >
                  <td className="px-3 py-2">{row.playDate} {row.timeFrom}</td>
                  <td className="px-3 py-2">{row.author?.name || '—'}</td>
                  <td className="px-3 py-2">{row.club?.name || '—'}</td>
                  <td className="px-3 py-2">{row.status}</td>
                  <td className="px-3 py-2">{row.responsesCount}</td>
                  <td className="px-3 py-2">{row.notifiedPlayersCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-lg shadow p-4 space-y-3 overflow-auto max-h-[70vh]">
          {!detail && !detailLoading && <div className="text-sm text-gray-500">Выберите предложение из списка.</div>}
          {detailLoading && <div className="text-sm text-gray-500">Загрузка detail...</div>}
          {detail && !detailLoading && (
            <>
              <div>
                <div className="text-sm text-gray-500">Offer</div>
                <div className="font-medium">{detail.id}</div>
                <div className="text-sm">{detail.playDate} {detail.timeFrom}-{detail.timeTo} • {detail.status}</div>
                <div className="text-sm">{detail.club?.name || '—'} • {detail.author?.name || '—'}</div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  disabled={actionLoading}
                  className="px-2 py-1 text-xs rounded bg-red-600 text-white disabled:opacity-50"
                  onClick={async () => {
                    const reason = askReason('Отмена предложения');
                    if (!reason) return;
                    await runAdminAction(`/play/admin/offers/${detail.id}/cancel`, { reason });
                  }}
                >
                  Cancel
                </button>
                <button
                  disabled={actionLoading}
                  className="px-2 py-1 text-xs rounded bg-amber-600 text-white disabled:opacity-50"
                  onClick={async () => {
                    const reason = askReason('Закрытие откликов');
                    if (!reason) return;
                    await runAdminAction(`/play/admin/offers/${detail.id}/close-responses`, { reason });
                  }}
                >
                  Close responses
                </button>
                <button
                  disabled={actionLoading}
                  className="px-2 py-1 text-xs rounded bg-slate-700 text-white disabled:opacity-50"
                  onClick={async () => {
                    const reason = askReason('Retry failed sends');
                    if (!reason) return;
                    await runAdminAction(`/play/admin/offers/${detail.id}/retry-failed-send`, { reason });
                  }}
                >
                  Retry failed send
                </button>
                <button
                  disabled={actionLoading}
                  className="px-2 py-1 text-xs rounded bg-slate-700 text-white disabled:opacity-50"
                  onClick={async () => {
                    const reason = askReason('Retry failed edits');
                    if (!reason) return;
                    await runAdminAction(`/play/admin/offers/${detail.id}/retry-failed-edit`, { reason });
                  }}
                >
                  Retry failed edit
                </button>
                {detail.author?.playerId && (
                  <>
                    <button
                      disabled={actionLoading}
                      className="px-2 py-1 text-xs rounded bg-red-700 text-white disabled:opacity-50"
                      onClick={async () => {
                        const reason = askReason('Block player in TTM PLAY');
                        if (!reason) return;
                        await runAdminAction(`/play/admin/players/${detail.author?.playerId}/block`, { reason });
                      }}
                    >
                      Block author
                    </button>
                    <button
                      disabled={actionLoading}
                      className="px-2 py-1 text-xs rounded bg-green-700 text-white disabled:opacity-50"
                      onClick={async () => {
                        const reason = askReason('Unblock player in TTM PLAY');
                        if (!reason) return;
                        await runAdminAction(`/play/admin/players/${detail.author?.playerId}/unblock`, { reason });
                      }}
                    >
                      Unblock author
                    </button>
                  </>
                )}
              </div>

              <div>
                <div className="text-sm font-medium mb-1">Responses ({detail.responsesCount})</div>
                <div className="space-y-1">
                  {(detail.responses || []).map((r) => (
                    <div key={r.id} className="text-sm border rounded px-2 py-1">
                      {r.player.name} • {r.player.levelCode || '—'} • PWR {r.player.pwr ?? '—'} • {r.status} • {fmt(r.respondedAt)}
                      {(detail.status === 'OPEN' || detail.status === 'AWAITING_SELECTION') && (
                        <button
                          disabled={actionLoading}
                          className="ml-2 px-2 py-0.5 text-xs rounded bg-blue-600 text-white disabled:opacity-50"
                          onClick={async () => {
                            const reason = askReason(`Выбор отклика ${r.player.name}`);
                            if (!reason) return;
                            await runAdminAction(`/play/admin/offers/${detail.id}/select`, { reason, responseId: r.id });
                          }}
                        >
                          Select
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-1">Deliveries ({detail.notifiedPlayersCount})</div>
                <div className="space-y-1">
                  {(detail.deliveries || []).map((d) => (
                    <div key={d.id} className="text-sm border rounded px-2 py-1">
                      wave#{d.waveNumber} • {d.recipientName || '—'} • {d.deliveryStatus}
                      {d.responseStatus ? ` • ${d.responseStatus}` : ''}
                      {d.lastError ? ` • err: ${d.lastError}` : ''}
                      {d.retryCount > 0 ? ` • retry:${d.retryCount}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayAdmin;

